import { css } from 'aphrodite';
import * as React from 'react';
import { DateInput } from '../../../web/components-v2/DateInput';
import { Input } from '../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { styleSheet } from './styles';

/** Sentinel value for the "All reps" creator option (vs. a specific creator id). */
export const ALL_CREATORS = 'all';

export interface ICreatorOption {
	/** The creator's `UserReference` id (the value matched against a conversation's `creator.id`). */
	id: string;
	/** Display label derived via the shared `getCreatorLabel` helper. */
	label: string;
}

interface IProps {
	/** Case-insensitive search text. */
	search: string;
	onSearchChange: (value: string) => void;
	/** Selected creator id, or `ALL_CREATORS` for no creator filter. */
	creatorId: string;
	onCreatorChange: (value: string) => void;
	/** Inclusive lower date bound as an ISO `YYYY-MM-DD` string, or '' for no bound. */
	startDate: string;
	onStartDateChange: (value: string) => void;
	/** Inclusive upper date bound as an ISO `YYYY-MM-DD` string, or '' for no bound. */
	endDate: string;
	onEndDateChange: (value: string) => void;
	/** Distinct creators derived from the full (unfiltered) conversation list. */
	creatorOptions: ICreatorOption[];
}

/**
 * Controlled, presentational filter bar for the coaching review list: a search box, a rep
 * (creator) dropdown, and a start/end date range. Holds no state of its own — the parent
 * (`ConversationList`) owns the filter state and applies the filters to the loaded data.
 */
export function ListFilters({
	search,
	onSearchChange,
	creatorId,
	onCreatorChange,
	startDate,
	onStartDateChange,
	endDate,
	onEndDateChange,
	creatorOptions,
}: IProps) {
	// Include the "All reps" sentinel so the Select can resolve a label for the unfiltered state.
	const selectItems = React.useMemo(
		() => [{ value: ALL_CREATORS, label: 'All reps' }, ...creatorOptions.map(o => ({ value: o.id, label: o.label }))],
		[creatorOptions]
	);

	return (
		<div className={css(styleSheet.filters)}>
			<Input
				value={search}
				onChange={e => onSearchChange(e.target.value)}
				placeholder='Search by title or rep'
				aria-label='Search conversations'
			/>
			<Select value={creatorId} onValueChange={onCreatorChange}>
				<SelectTrigger aria-label='Filter by rep' styles={styleSheet.repSelectTrigger}>
					<SelectValue placeholder='All reps' />
				</SelectTrigger>
				<SelectContent>
					{selectItems.map(item => (
						<SelectItem key={item.value} value={item.value}>
							{item.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<div className={css(styleSheet.filterDateRange)}>
				<DateInput value={startDate} onChange={onStartDateChange} />
				<DateInput value={endDate} onChange={onEndDateChange} />
			</div>
		</div>
	);
}
